import React from "react";
import { Modal } from "../../components/ui/Modal";

interface ContentTypeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPost: () => void;
  onSelectReel: () => void;
}

export const ContentTypeSelector: React.FC<ContentTypeSelectorProps> = ({
  isOpen,
  onClose,
  onSelectPost,
  onSelectReel,
}) => {
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Content"
      size="md"
      className="max-w-2xl"
    >
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            What would you like to create?
          </h2>
          <p className="text-gray-600">
            Choose the type of content you want to create for Instagram.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Post Option */}
          <button
            onClick={onSelectPost}
            className="group relative bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 text-left"
          >
            <div className="flex items-start space-x-4">
              <div className="shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <svg
                    className="w-6 h-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Instagram Post
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Create a traditional Instagram feed post with images or videos in 4:5 aspect ratio.
                </p>
                <div className="space-y-1 text-xs text-gray-500">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Single image or carousel (up to 10 items)
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    4:5 aspect ratio (square-ish)
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Appears in feed and grid
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    Supports captions and hashtags
                  </div>
                </div>
              </div>
            </div>
            
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>

          {/* Reel Option */}
          <button
            onClick={onSelectReel}
            className="group relative bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 text-left"
          >
            <div className="flex items-start space-x-4">
              <div className="shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                  <svg
                    className="w-6 h-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Instagram Reel
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Create a vertical video reel with a custom thumbnail in 9:16 aspect ratio.
                </p>
                <div className="space-y-1 text-xs text-gray-500">
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    One video + one thumbnail image
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    9:16 aspect ratio (vertical)
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Separate reels tab (not in grid)
                  </div>
                  <div className="flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Supports captions and hashtags
                  </div>
                </div>
              </div>
            </div>
            
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg
                className="w-5 h-5 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </button>
        </div>

        {/* Stories Note */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-start space-x-3">
            <div className="shrink-0">
              <svg
                className="w-5 h-5 text-gray-400 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-1">
                Instagram Stories
              </h4>
              <p className="text-sm text-gray-600">
                Stories are created automatically when a story video (9:16) is approved in the Library. Schedule and publish them from the Stories tab.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-8">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};